import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import request from '../helpers/request';

const VIEW_STORAGE_KEY = 'functions-view-preference';

export const useFunctions = () => {
  // Functions state
  const [functions, setFunctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [runningFunctions, setRunningFunctions] = useState(new Set());

  // Selected function for editing/viewing
  const [selectedFunction, setSelectedFunction] = useState(null);
  const [selectedFunctionLoading, setSelectedFunctionLoading] = useState(false);

  // Logs state
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logFilters, setLogFilters] = useState({
    search: '',
    errorFilter: 'all',
    day: '',
    page: 1,
    pageSize: 20,
    total: 0,
  });

  // View preference (grid/list)
  const [view, setView] = useState(() => {
    return localStorage.getItem(VIEW_STORAGE_KEY) || 'grid';
  });

  // Persist view preference
  const handleSetView = useCallback((newView) => {
    setView(newView);
    localStorage.setItem(VIEW_STORAGE_KEY, newView);
  }, []);

  // Fetch all functions
  const fetchFunctions = useCallback(async () => {
    try {
      setRefreshing(true);
      setError(null);
      const response = await request.get('api/v1/functions/');
      setFunctions(response.data || []);
    } catch (err) {
      console.error('Error fetching functions:', err);
      setError('Failed to load functions');
      toast.error('Failed to load functions');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Get single function details
  const getFunction = useCallback(async (id) => {
    try {
      setSelectedFunctionLoading(true);
      const response = await request.get(`api/v1/functions/${id}/`);
      setSelectedFunction(response.data);
      return response.data;
    } catch (err) {
      console.error('Error fetching function:', err);
      toast.error('Failed to load function details');
      return null;
    } finally {
      setSelectedFunctionLoading(false);
    }
  }, []);

  // Create function
  const createFunction = useCallback(async (data) => {
    try {
      await request.post('api/v1/functions/', data);
      toast.success('Function created successfully');
      await fetchFunctions();
      return true;
    } catch (err) {
      console.error('Error creating function:', err);
      toast.error(err.response?.data?.error || 'Failed to create function');
      return false;
    }
  }, [fetchFunctions]);

  // Update function
  const updateFunction = useCallback(async (id, data) => {
    try {
      const { logs, ...payload } = data || {};
      await request.put(`api/v1/functions/${id}/`, payload);
      toast.success('Function updated successfully');
      await fetchFunctions();
      return true;
    } catch (err) {
      console.error('Error updating function:', err);
      toast.error(err.response?.data?.error || 'Failed to update function');
      return false;
    }
  }, [fetchFunctions]);

  // Delete function
  const deleteFunction = useCallback(async (id) => {
    try {
      await request.delete(`api/v1/functions/${id}/`);
      toast.success('Function deleted successfully');
      await fetchFunctions();
      return true;
    } catch (err) {
      console.error('Error deleting function:', err);
      toast.error('Failed to delete function');
      return false;
    }
  }, [fetchFunctions]);

  // Duplicate function
  const duplicateFunction = useCallback(async (func) => {
    try {
      const newData = {
        name: `${func.name} (Copy)`,
        code: func.code,
        description: func.description,
        function_type: func.function_type,
        interval: func.interval,
      };
      await request.post('api/v1/functions/', newData);
      toast.success('Function duplicated successfully');
      await fetchFunctions();
      return true;
    } catch (err) {
      console.error('Error duplicating function:', err);
      toast.error(err.response?.data?.error || 'Failed to duplicate function');
      return false;
    }
  }, [fetchFunctions]);

  // Run function
  const runFunction = useCallback(async (id) => {
    try {
      setRunningFunctions(prev => new Set(prev).add(id));
      await request.post(`api/v1/functions/${id}/run/`);
      toast.success('Function executed successfully');
      await fetchFunctions();
      return true;
    } catch (err) {
      console.error('Error running function:', err);
      toast.error(err.response?.data?.error || 'Failed to execute function');
      return false;
    } finally {
      setRunningFunctions(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  }, [fetchFunctions]);

  // Fetch logs for a function
  const fetchLogs = useCallback(async (functionId, filters = {}) => {
    try {
      setLogsLoading(true);
      const params = new URLSearchParams({
        page: filters.page || logFilters.page,
        page_size: filters.pageSize || logFilters.pageSize,
        ...(filters.search && { search: filters.search }),
        ...(filters.errorFilter && filters.errorFilter !== 'all' && { log_level: filters.errorFilter }),
        ...(filters.day && { date: filters.day }),
      });
      const { data } = await request.get(`api/v1/functions/${functionId}/logs/?${params}`);
      setLogs(data.results || []);
      setLogFilters(prev => ({
        ...prev,
        total: data.count || 0,
        ...(filters.page && { page: filters.page }),
        ...(filters.pageSize && { pageSize: filters.pageSize }),
        ...(filters.search !== undefined && { search: filters.search }),
        ...(filters.errorFilter && { errorFilter: filters.errorFilter }),
        ...(filters.day !== undefined && { day: filters.day }),
      }));
      return data;
    } catch (err) {
      console.error('Error fetching logs:', err);
      toast.error('Failed to load logs');
      return null;
    } finally {
      setLogsLoading(false);
    }
  }, [logFilters.page, logFilters.pageSize]);

  // Delete selected logs
  const deleteLogs = useCallback(async (functionId, logIds) => {
    try {
      const { data: func } = await request.get(`api/v1/functions/${functionId}/`);
      const allLogs = func.logs || [];
      const toDeleteKeys = new Set(logIds);
      const updatedLogs = allLogs.filter((_, idx) => !toDeleteKeys.has(idx));
      await request.put(`api/v1/functions/${functionId}/`, { ...func, logs: updatedLogs });
      toast.success(`${logIds.length} log(s) deleted`);
      return true;
    } catch (err) {
      console.error('Error deleting logs:', err);
      toast.error('Failed to delete logs');
      return false;
    }
  }, []);

  // Delete all logs
  const deleteAllLogs = useCallback(async (functionId) => {
    try {
      const { data: func } = await request.get(`api/v1/functions/${functionId}/`);
      await request.put(`api/v1/functions/${functionId}/`, { ...func, logs: [] });
      toast.success('All logs deleted');
      return true;
    } catch (err) {
      console.error('Error deleting all logs:', err);
      toast.error('Failed to delete all logs');
      return false;
    }
  }, []);

  // Mark logs as read
  const markLogsAsRead = useCallback(async (functionId) => {
    try {
      const func = functions.find(f => f.id === functionId);
      if (func && Array.isArray(func.logs) && func.logs.some(l => l.unread)) {
        const updatedLogs = func.logs.map(l => ({ ...l, unread: false }));
        await request.put(`api/v1/functions/${functionId}/`, { ...func, logs: updatedLogs });
        setFunctions(prev =>
          prev.map(f =>
            f.id === functionId ? { ...f, logs: updatedLogs } : f
          )
        );
      }
    } catch (err) {
      console.error('Error marking logs as read:', err);
    }
  }, [functions]);

  // Check if function is running
  const isRunning = useCallback((id) => runningFunctions.has(id), [runningFunctions]);

  // Get function status info
  const getFunctionStatus = useCallback((func) => {
    if (!func) return { status: 'unknown', color: 'neutral' };
    
    if (runningFunctions.has(func.id)) {
      return { status: 'running', color: 'primary', label: 'Running' };
    }

    const hasUnreadLogs = Array.isArray(func.logs) && func.logs.some(l => l.unread);
    const lastLog = Array.isArray(func.logs) && func.logs.length > 0 ? func.logs[func.logs.length - 1] : null;
    const hasError = lastLog?.level === 'error';

    if (hasError) {
      return { status: 'error', color: 'danger', label: 'Error' };
    }

    if (func.last_executed) {
      return { status: 'success', color: 'success', label: 'Success', hasUnread: hasUnreadLogs };
    }

    return { status: 'idle', color: 'neutral', label: 'Never run' };
  }, [runningFunctions]);

  // Validate function name
  const validateFunctionName = useCallback((name, isEdit = false, editingId = null) => {
    if (!name) {
      return 'Function name is required';
    }

    if (!/^[a-zA-Z_][a-zA-Z0-9_ ]*$/.test(name)) {
      return 'Function name must start with a letter or underscore and contain only letters, numbers, underscores, and spaces';
    }

    const nameExists = functions.some(func =>
      func.name.toLowerCase() === name.toLowerCase() &&
      (isEdit ? func.id !== editingId : true)
    );

    if (nameExists) {
      return 'A function with this name already exists';
    }

    return '';
  }, [functions]);

  return {
    // Functions data
    functions,
    loading,
    refreshing,
    error,
    runningFunctions,
    
    // Selected function
    selectedFunction,
    selectedFunctionLoading,
    setSelectedFunction,
    
    // CRUD operations
    fetchFunctions,
    getFunction,
    createFunction,
    updateFunction,
    deleteFunction,
    duplicateFunction,
    runFunction,
    
    // Logs
    logs,
    logsLoading,
    logFilters,
    setLogFilters,
    fetchLogs,
    deleteLogs,
    deleteAllLogs,
    markLogsAsRead,
    
    // Utilities
    isRunning,
    getFunctionStatus,
    validateFunctionName,
    
    // View preference
    view,
    setView: handleSetView,
  };
};

export default useFunctions;
